import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
    Search,
    Building2,
    Inbox,
    Hash,
    CreditCard,
    CheckCircle2,
    XCircle,
    CalendarDays,
    CalendarClock,
    Wallet,
    Banknote,
    ListOrdered,
    FileText,
    Paperclip,
} from "lucide-react";
import AppContext from "../../context/AppContext";
import { installmentListApi } from "../../service/CustomerSupport";
import translations from "../common/Translations";
import moment from "moment";
import { toast } from "../../utils/toast";
import CommonButton from "../common/CommonButton";
import { InactivePlanDetailModal } from "./InactivePlanDetailModal";
import { canShowActionColumn } from "../../utils/permissions";
import useDirection from "../../hooks/useDirection";
import { getPlanAttachmentUrls } from "../../utils/attachments";

// PDFs open directly; images are wrapped in a bare page so they scale to the tab.
const openPlanAttachment = (fileUrl) => {
    const isPDF = fileUrl.toLowerCase().split("?")[0].endsWith(".pdf");
    if (isPDF) {
        window.open(fileUrl, "_blank", "noopener,noreferrer");
        return;
    }
    const newTab = window.open("", "_blank");
    if (newTab) {
        newTab.document.write(`<img src="${fileUrl}" style="width:100%;height:auto;" />`);
    }
};

// One chip per URL in the plan's `attachments` array, numbered only when
// there is more than one.
const PlanAttachmentLinks = ({ plan, langMode, emptyFallback }) => {
    const urls = getPlanAttachmentUrls(plan);
    if (urls.length === 0) return emptyFallback;
    return (
        <span className="inline-flex flex-wrap gap-1.5">
            {urls.map((url, i) => (
                <span
                    key={`${url}-${i}`}
                    onClick={() => openPlanAttachment(url)}
                    className="inline-block cursor-pointer rounded-md bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-600"
                >
                    {translations[langMode].viewattachment}
                    {urls.length > 1 ? ` ${i + 1}` : ""}
                </span>
            ))}
        </span>
    );
};

// Detail-tile styling, ported from the same recipe used by Account History's
// "Account Details" card (src/pages/TaskHistory/TaskHistory.js:29-59) so
// View Installment's plan-details card matches the Account & Reports Center
// look. Kept local to this file rather than shared, following this
// codebase's precedent of per-file duplication for small presentational
// helpers (e.g. HoverTooltip is separately defined in several toolbar files).
const detailCardClass = (theme, wide) =>
    `flex items-start gap-2.5 rounded-[10px] border p-3 ${wide ? "sm:col-span-2" : ""} ${theme === "dark"
        ? "border-gray-600 bg-gray-700/40"
        : theme === "high-contrast"
            ? "high-contrast border-2"
            : "border-gray-200 bg-gray-50"
        }`;

const detailIconChipClass = (theme) =>
    `flex h-7 w-7 shrink-0 items-center justify-center rounded-[7px] ${theme === "dark"
        ? "bg-gray-600/70 text-gray-300"
        : theme === "high-contrast"
            ? "high-contrast"
            : "border border-gray-200 bg-white text-[#236DB4]"
        }`;

const detailLabelClass = (theme) =>
    `mb-0.5 text-[11px] font-semibold uppercase tracking-wide ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`;

const detailValueClass = (theme) =>
    `text-sm font-semibold break-words ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`;

// Same "Card 2" recipe used by Account History's Account Details card
// (src/pages/TaskHistory/TaskHistory.js) -- View Installment now renders
// as its own stack of independent cards (no shared outer card from
// CustomerSupport.jsx for this tab), so this section needs its own border
// again to stand on its own like the other cards below it.
const cardClass = (theme) =>
    `rounded-[14px] border ${theme === "dark"
        ? "bg-gray-800/60 border-gray-700"
        : theme === "high-contrast"
            ? "high-contrast border-2"
            : "bg-white border-gray-200 shadow-[0_1px_2px_rgba(16,24,40,0.05),0_1px_3px_rgba(16,24,40,0.05)]"
    }`;

// Same rounded-xl bordered card recipe used for tables across Account
// History/Export Report (src/pages/TaskHistory/TaskHistory.js,
// src/pages/worklog/ExportData.js).
const tableCardClass = (theme) =>
    `flex flex-col rounded-xl border overflow-hidden ${theme === "dark"
        ? "border-gray-700 bg-gray-800/50"
        : theme === "high-contrast"
            ? "high-contrast border-2"
            : "border-slate-200 bg-white shadow-sm"
    }`;

const tableHeadClass = (theme) =>
    `sticky top-0 z-10 border-b ${theme === "dark"
        ? "border-gray-700 bg-gray-800 text-gray-200"
        : theme === "high-contrast"
            ? "high-contrast bg-gray-100"
            : "border-slate-200 bg-slate-50 text-slate-500"
    }`;

const tableRowClass = (theme) =>
    `border-b ${theme === "dark" ? "border-gray-700 hover:bg-gray-700/50" : "border-slate-100 hover:bg-slate-50/70"}`;

const thClass = "px-5 py-3.5 text-left rtl:text-right font-semibold text-xs uppercase tracking-[0.04em]";
const tdClass = "px-5 py-3.5";
// Same lead-column-vs-muted-supporting-column split Export Report uses
// between its "Workflow Names" column and its "No."/"Requested At" columns.
const tdPrimaryClass = (theme) => `px-5 py-3.5 ${theme === "dark" ? "text-gray-200" : "text-slate-700"}`;
const tdSecondaryClass = (theme) => `px-5 py-3.5 tabular-nums ${theme === "dark" ? "text-gray-400" : "text-slate-500"}`;

// Compact table-title-bar search box, same icon-prefixed visual recipe as
// Export Report's inline table search (src/pages/worklog/ExportData.js:904-915),
// sized to sit in a card header instead of a column header.
const TableSearchInput = ({ value, onChange, placeholder, theme, isRtl }) => (
    <div className="relative w-[160px] shrink-0 sm:w-[200px]">
        <Search
            className={`absolute ${isRtl ? "right-2.5" : "left-2.5"} top-1/2 h-3.5 w-3.5 -translate-y-1/2 ${theme === "dark" ? "text-gray-500" : "text-gray-400"}`}
            aria-hidden
        />
        <input
            type="text"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className={`h-8 w-full rounded-lg border text-xs ${isRtl ? "pr-8 pl-2 text-right" : "pl-8 pr-2"} focus:outline-none focus:ring-0 ${theme === "dark"
                ? "border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500"
                : "border-gray-300 bg-white text-gray-800 placeholder-gray-400"
                }`}
        />
    </div>
);

// Empty-tbody state, ported from Export Report's table
// (src/pages/worklog/ExportData.js:934-942) so a filtered-to-zero or
// genuinely-empty table reads as "no data" instead of a bare header with
// nothing below it.
const EmptyTableRow = ({ colSpan, message, theme }) => (
    <tr>
        <td colSpan={colSpan} className="px-5 py-8">
            <div className={`flex flex-col items-center justify-center gap-1 text-center ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`}>
                <Inbox size={24} strokeWidth={1.5} className="mb-1 opacity-70" />
                <span className="text-xs">{message}</span>
            </div>
        </td>
    </tr>
);

const ViewInstallment = ({ onHeaderActionsChange }) => {
    const tableRef = useRef();
    const { theme, langMode } = useContext(AppContext);
    const { isRTL: isRtl } = useDirection();
    const showInstallmentActionColumn = canShowActionColumn("INSTALMENT_PLAN");
    const [accountNo, setAccountNo] = useState('');
    const [installmentList, setInstallmentList] = useState([]);
    const [inactivePlans, setInactivePlans] = useState([]);
    const [planDetails, setPlanDetails] = useState(null);
    const [selectedInactivePlanDetail, setSelectedInactivePlanDetail] = useState(null);
    // Local, client-side-only search filters for each table -- nothing here
    // touches installmentListApi or any other request, same as Export
    // Report's own inline table search (filteredReportData).
    const [customerDetailsSearch, setCustomerDetailsSearch] = useState("");
    const [inactivePlansSearch, setInactivePlansSearch] = useState("");
    const [activeInstallmentsSearch, setActiveInstallmentsSearch] = useState("");

    const handleInputChange = (event) => {
        setAccountNo(event.target.value);
    };

    const fetchInstallment = useCallback(async () => {
        if (!accountNo) {
            return;
        }
        try {
            const data = await installmentListApi(accountNo, ((localStorage.getItem("CustprojectId") || "").replace(/"/g, "")));

            if (data?.header?.code === 600 && data?.body?.value) {
                const { details = [], inActivePlan = [], ...rest } = data.body.value;
                setInstallmentList(details);
                setInactivePlans(inActivePlan);
                setPlanDetails(rest);
            } else {
                setInstallmentList([]);
                setInactivePlans([]);
                setPlanDetails(null);
                toast.error(data?.body?.value);
            }
            setSelectedInactivePlanDetail(null);
        } catch (error) {
            console.error("Error fetching installments:", error);
            setSelectedInactivePlanDetail(null);
        }
    }, [accountNo]);

    console.log('inactivePlans', inactivePlans)
    const handleViewInactivePlanDetail = useCallback((plan) => {
        setSelectedInactivePlanDetail(plan);
    }, []);

    // Function to close the modal
    const handleCloseModal = useCallback(() => {
        setSelectedInactivePlanDetail(null);
    }, []);

    const handleSearch = useCallback(() => {
        if (accountNo.trim() === "") {
            toast.error(translations[langMode].plzwentervalidacc);
            return;
        }
        fetchInstallment();
    }, [fetchInstallment]);


    const formatDate = (dateString) => {
        if (!dateString) {
            return "-";
        }
        const date = new Date(dateString);
        const options = {
            day: 'numeric',
            month: 'short',
            year: 'numeric',

        };

        const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || 'UTC';
        options.timeZone = timeZone;
        return date.toLocaleString('en-US', options);
    };


    const convertToArabicNumbers = (phoneNo) => {
        const digits = phoneNo?.replace(/\D/g, '');
        const countryCode = digits?.slice(0, 3);
        const part1 = digits?.slice(3, 7);
        const part2 = digits?.slice(7);
        return `+(${countryCode}) ${part1} ${part2}`;
    };


    const handlePrint = () => {
        if (!tableRef.current) {
            toast.error("Nothing to print!");
            return;
        }

        const printContent = tableRef.current.innerHTML;
        const printWindow = window.open('', '', 'width=800,height=600');
        printWindow.document.write(`
        <html>
            <head>
                <title>Print Installment Details</title>
                <style>
                    body {
                        font-family: MediumFont, Manrope, sans-serif;
                        margin: 20px;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                    }
                    th, td {
                        border: 1px solid #ccc;
                        padding: 8px;
                        text-align: left;
                    }
                    th {
                        background-color: #f2f2f2;
                    }
                </style>
            </head>
            <body>
                ${printContent}
            </body>
        </html>
    `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    };

    const customerDetailsQuery = customerDetailsSearch.trim().toLowerCase();
    const customerDetailsMatches =
        !customerDetailsQuery ||
        [planDetails?.createdByName, planDetails?.accountNo, planDetails?.civilId, planDetails?.phoneNo].some((v) =>
            String(v ?? "").toLowerCase().includes(customerDetailsQuery)
        );

    const inactivePlansQuery = inactivePlansSearch.trim().toLowerCase();
    const filteredInactivePlans = inactivePlans.filter((plan) => {
        if (!inactivePlansQuery) return true;
        return [plan.id, plan.accountNo, formatDate(plan.createdDate), plan.advanceAmount, plan?.remark].some((v) =>
            String(v ?? "").toLowerCase().includes(inactivePlansQuery)
        );
    });

    const activeInstallmentsQuery = activeInstallmentsSearch.trim().toLowerCase();
    const filteredInstallmentList = installmentList.filter((item) => {
        if (!activeInstallmentsQuery) return true;
        return [item.detailId, item.paymentAmount, formatDate(item.paymentDate), formatDate(item.paymentStatusUpdatedAt), item.liveOutstanding, item.isPaid ? "Paid" : "Pending"].some((v) =>
            String(v ?? "").toLowerCase().includes(activeInstallmentsQuery)
        );
    });

    // Pushed up into CustomerSupport.jsx's header (heading left, these
    // controls right) via the onHeaderActionsChange slot, since this state/
    // logic lives here but the header itself is rendered one level up.
    // Same "child pushes a control into a parent-owned header" pattern as
    // ExportData.js's setHeaderActivitySelect, scoped locally instead of via
    // AppContext. Cleanup on unmount (tab switch away) clears the slot.
    useEffect(() => {
        if (!onHeaderActionsChange) return undefined;

        onHeaderActionsChange(
            <div className="flex w-full flex-row flex-nowrap items-center gap-1.5 sm:w-auto sm:gap-2">
                <div className="relative min-w-0 flex-1 sm:w-[220px] sm:flex-none">
                    <Search
                        className={`absolute ${isRtl ? "right-2.5 sm:right-3" : "left-2.5 sm:left-3"} top-1/2 -translate-y-1/2 ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}
                        size={16}
                    />
                    <input
                        id="account-search"
                        autoComplete="new-password"
                        type="text"
                        className={`h-10 w-full min-w-0 box-border rounded-[10px] border text-sm leading-normal ${isRtl ? "pr-8 pl-2 sm:pr-10 sm:pl-3 text-right" : "pl-8 pr-2 sm:pl-10 sm:pr-3"} ${theme === "dark"
                            ? "border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-400"
                            : theme === "high-contrast"
                                ? "high-contrast"
                                : "border-gray-300 bg-white text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            }`}
                        placeholder={translations[langMode].enteraccno}
                        value={accountNo}
                        onChange={handleInputChange}
                    />
                </div>
                <CommonButton colorIntent="primary"
                    onClick={handleSearch}
                    variant="primary"
                    tooltipText={translations[langMode].search}
                    tooltipPosition="top"
                    className="shrink-0 justify-center !h-10 !min-h-[40px] !rounded-[10px] px-2.5 py-2 text-xs sm:px-4 sm:text-sm"
                >
                    <Search size={15} className="shrink-0" />
                    {translations[langMode].search}
                </CommonButton>
                <CommonButton colorIntent="primary"
                    onClick={() => handlePrint()}
                    tooltipText={translations[langMode].printinstallmentdetails}
                    tooltipPosition="top"
                    variant="primary"
                    className="shrink-0 justify-center !h-10 !min-h-[40px] !rounded-[10px] px-2.5 py-2 text-xs sm:px-4 sm:text-sm"
                >
                    {translations[langMode].Print}
                </CommonButton>
            </div>
        );

        return () => onHeaderActionsChange(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountNo, theme, isRtl, langMode]);

    return (
        <>
        <div ref={tableRef} className="flex flex-col gap-3">
                <div className={`min-w-0 ${cardClass(theme)}`}>
                    <div className={`px-5 pt-6 ${theme === "dark" ? "border-gray-700" : "border-slate-100"}`}>
                        <h3 className={`text-sm font-semibold break-words ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`}>
                            {translations[langMode].instplanDetail}
                        </h3>
                    </div>
                    <div className="p-4 sm:p-5">
                    {planDetails ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><Hash size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].planId}</p>
                                    <p className={detailValueClass(theme)}>{planDetails.planId ?? "-"}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><CreditCard size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].accno}</p>
                                    <p className={detailValueClass(theme)}>{planDetails.accountNo ?? "-"}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}>
                                    {planDetails.isActive ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
                                </span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].planActive}</p>
                                    <p className={`text-sm font-bold ${planDetails.isActive ? "text-green-600" : "text-red-600"}`}>
                                        {planDetails.isActive ? "Yes" : "No"}
                                    </p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><CalendarDays size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].planCreated}</p>
                                    <p className={detailValueClass(theme)}>{formatDate(planDetails.createdDate)}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><Wallet size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].outstaningAmt}</p>
                                    <p className={detailValueClass(theme)}>{planDetails.outstandingAtCreation ?? "-"}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><Banknote size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].Advamt}</p>
                                    <p className={detailValueClass(theme)}>{planDetails.advanceAmount ?? "-"}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><CalendarDays size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].AdvDate}</p>
                                    <p className={detailValueClass(theme)}>{formatDate(planDetails.advancePaymentDate)}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><CalendarClock size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].firstInsDate}</p>
                                    <p className={detailValueClass(theme)}>{formatDate(planDetails.firstInstalmentDate)}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><ListOrdered size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].noOfInst}</p>
                                    <p className={detailValueClass(theme)}>{planDetails.noOfInstalments ?? "-"}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme, true)}>
                                <span className={detailIconChipClass(theme)}><FileText size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].remark}</p>
                                    <p className={detailValueClass(theme)}>{planDetails.remarks || "-"}</p>
                                </div>
                            </div>
                            <div className={detailCardClass(theme)}>
                                <span className={detailIconChipClass(theme)}><Paperclip size={14} /></span>
                                <div className="min-w-0">
                                    <p className={detailLabelClass(theme)}>{translations[langMode].Attachment}</p>
                                    <PlanAttachmentLinks
                                        plan={planDetails}
                                        langMode={langMode}
                                        emptyFallback={<p className={detailValueClass(theme)}>N/A</p>}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="flex min-h-[220px] w-full flex-col items-center justify-center gap-1.5 p-6 text-center sm:p-12">
                            <Building2 size={30} strokeWidth={1.5} className={`mb-1 ${theme === "dark" ? "text-gray-600" : "text-gray-300"}`} />
                            <p className={`max-w-xs text-sm leading-snug ${theme === "dark" ? "text-gray-400" : "text-gray-500"}`}>
                                {translations[langMode]?.viewInstallmentEmptySub ?? "Search an account number above to view its installment plan."}
                            </p>
                        </div>
                    )}
                    </div>
                </div>

                {planDetails && (
                    <>
                        <div className={tableCardClass(theme)}>
                            <div className={`flex items-center gap-3 px-5 py-3 border-b ${theme === "dark" ? "border-gray-700" : "border-slate-100"}`}>
                                <h3 className={`text-sm font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`}>
                                    {translations[langMode].customerDetails}
                                </h3>
                                <TableSearchInput
                                    value={customerDetailsSearch}
                                    onChange={(e) => setCustomerDetailsSearch(e.target.value)}
                                    placeholder={translations[langMode].search}
                                    theme={theme}
                                    isRtl={isRtl}
                                />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full table-auto whitespace-nowrap text-xs">
                                    <thead className={tableHeadClass(theme)}>
                                        <tr>
                                            <th className={thClass}>{translations[langMode].created_By}</th>
                                            <th className={thClass}>{translations[langMode].accountNo}</th>
                                            <th className={thClass}>{translations[langMode].civilId}</th>
                                            <th className={thClass}>{translations[langMode].phoneNo}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customerDetailsMatches ? (
                                            <tr className={tableRowClass(theme)}>
                                                <td className={tdPrimaryClass(theme)}>{planDetails?.createdByName || "-"}</td>
                                                <td className={tdSecondaryClass(theme)}>{planDetails?.accountNo || "-"}</td>
                                                <td className={tdSecondaryClass(theme)}>{planDetails?.civilId || "-"}</td>
                                                <td className={tdSecondaryClass(theme)}>{planDetails?.phoneNo ? convertToArabicNumbers(planDetails.phoneNo) : "-"}</td>
                                            </tr>
                                        ) : (
                                            <EmptyTableRow
                                                colSpan={4}
                                                theme={theme}
                                                message={translations[langMode]?.noMatchingCustomerRecord ?? "No matching customer record."}
                                            />
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className={tableCardClass(theme)}>
                            <div className={`flex items-center gap-3 px-5 py-3 border-b ${theme === "dark" ? "border-gray-700" : "border-slate-100"}`}>
                                <h3 className={`text-sm font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`}>
                                    {translations[langMode].inactiveplane}
                                </h3>
                                <TableSearchInput
                                    value={inactivePlansSearch}
                                    onChange={(e) => setInactivePlansSearch(e.target.value)}
                                    placeholder={translations[langMode].search}
                                    theme={theme}
                                    isRtl={isRtl}
                                />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full table-auto whitespace-nowrap text-xs">
                                    <thead className={tableHeadClass(theme)}>
                                        <tr>
                                            <th className={thClass}>{translations[langMode].planId}</th>
                                            <th className={thClass}>{translations[langMode].accno}</th>
                                            <th className={thClass}>{translations[langMode].cretDate}</th>
                                            <th className={thClass}>{translations[langMode].Advamt}</th>
                                            <th className={thClass}>{translations[langMode].remark}</th>
                                            <th className={thClass}>{translations[langMode].Attachment}</th>
                                            {showInstallmentActionColumn && <th className={thClass}>Action</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInactivePlans.length === 0 && (
                                            <EmptyTableRow
                                                colSpan={showInstallmentActionColumn ? 7 : 6}
                                                theme={theme}
                                                message={translations[langMode]?.noInactiveInstallmentPlans ?? "No inactive installment plans."}
                                            />
                                        )}
                                        {filteredInactivePlans.map((plan, index) => (
                                            <tr key={index} className={tableRowClass(theme)}>
                                                <td className={tdSecondaryClass(theme)}>{plan.id ?? "-"}</td>
                                                <td className={tdPrimaryClass(theme)}>{plan.accountNo || "-"}</td>
                                                <td className={tdSecondaryClass(theme)}>
                                                    {formatDate(plan.createdDate)}
                                                </td>
                                                <td className={tdSecondaryClass(theme)}>OMR {plan.advanceAmount.toFixed(2)}</td>
                                                <td className={tdSecondaryClass(theme)}>{plan?.remark || 'N/A'}</td>
                                                <td className={getPlanAttachmentUrls(plan).length ? tdClass : tdSecondaryClass(theme)}>
                                                    <PlanAttachmentLinks plan={plan} langMode={langMode} emptyFallback="N/A" />
                                                </td>
                                                {showInstallmentActionColumn && (
                                                    <td className={tdClass}>
                                                        <CommonButton colorIntent="primary"
                                                            onClick={() => handleViewInactivePlanDetail(plan)}
                                                            variant="primary"
                                                            className="!h-8 !min-h-[32px] !rounded-[8px] px-3 text-xs"
                                                        >
                                                            {translations[langMode].viewdetails}
                                                        </CommonButton>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className={tableCardClass(theme)}>
                            <div className={`flex items-center gap-3 px-5 py-3 border-b ${theme === "dark" ? "border-gray-700" : "border-slate-100"}`}>
                                <h3 className={`text-sm font-semibold ${theme === "dark" ? "text-gray-100" : "text-gray-800"}`}>
                                    {translations[langMode].activeInst}
                                </h3>
                                <TableSearchInput
                                    value={activeInstallmentsSearch}
                                    onChange={(e) => setActiveInstallmentsSearch(e.target.value)}
                                    placeholder={translations[langMode].search}
                                    theme={theme}
                                    isRtl={isRtl}
                                />
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full table-auto whitespace-nowrap text-xs">
                                    <thead className={tableHeadClass(theme)}>
                                        <tr>
                                            <th className={thClass}>{translations[langMode].DetailId}</th>
                                            <th className={thClass}>{translations[langMode].PayAmt}</th>
                                            <th className={thClass}>{translations[langMode].dueDate}</th>
                                            <th className={thClass}>{translations[langMode].paymentStatusUpdatedAt}</th>
                                            <th className={thClass}>{translations[langMode].outstandingForInstalmentPlan}</th>
                                            <th className={thClass}>{translations[langMode].Status}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredInstallmentList.length === 0 && (
                                            <EmptyTableRow
                                                colSpan={6}
                                                theme={theme}
                                                message={translations[langMode]?.noActiveInstallments ?? "No active installments."}
                                            />
                                        )}
                                        {filteredInstallmentList.map((item, index) => (
                                            <tr key={index} className={tableRowClass(theme)}>
                                                <td className={tdSecondaryClass(theme)}>{item.detailId ?? "-"}</td>
                                                <td className={tdPrimaryClass(theme)}>OMR {item.paymentAmount.toFixed(2)}</td>
                                                <td className={tdSecondaryClass(theme)}>
                                                    {formatDate(item.paymentDate)}
                                                </td>
                                                <td className={tdSecondaryClass(theme)}>
                                                    {formatDate(item.paymentStatusUpdatedAt)}
                                                </td>
                                                <td className={tdPrimaryClass(theme)}>
                                                    {item.liveOutstanding != null ? `OMR ${Number(item.liveOutstanding).toFixed(2)}` : "-"}
                                                </td>
                                                <td className={tdSecondaryClass(theme)}>
                                                    {item.isPaid ? "Paid" : "Pending"}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}
            </div>
            <InactivePlanDetailModal
                plan={selectedInactivePlanDetail}
                onClose={handleCloseModal}
                formatDate={formatDate}
                translations={translations}
                langMode={langMode}
            />
        </>
    );
};

export default ViewInstallment;
